import { computed } from 'vue'
import { useStore } from 'vuex'
import type { Conversation } from '@/types/dashboard'
import { useConversationStore } from '@/composables/useConversationStore'
import { resolveRoleScope } from '@/composables/useAdmin'
import { isFluxoHidden } from '@/composables/useHiddenFluxos'

const PRIO_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }

/**
 * Conversas ativas visíveis pro agente logado, respeitando a regra de visão do perfil
 * (geral.visaoSomenteAgente / geral.visaoAgenteETime / geral.visaoTodos — a mais restritiva
 * vence, exceto Administrador/EC - ISBET, travados como irrestritos pelo nome — ver
 * resolveRoleScope) e ordenadas por prioridade. Mesma lógica usada em ConvDrawer.vue —
 * extraída pra cá pra não duplicar (e divergir) numa terceira tela (Modo Foco).
 */
export function useMyConversations() {
  const store = useStore()
  const { conversations } = useConversationStore()
  const authUser    = computed(() => store.getters.authUser)
  const userTeamIds = computed<number[]>(() => store.getters.userTeamIds)

  const myConvs = computed<Conversation[]>(() => {
    if (!authUser.value) return []
    const uid    = authUser.value.id
    const active = conversations.value.filter(c =>
      c.status !== 'resolved' && !c.labels.includes('avaliacao_atendimento') && !isFluxoHidden(c.fluxo_id)
    )
    const scope  = resolveRoleScope(uid, userTeamIds.value)

    let result: typeof active
    if (scope.unrestricted) {
      result = active
    } else if (scope.hasSomenteAgente) {
      result = active.filter(c => c.assignee?.id === uid)
    } else if (scope.hasAgenteETime) {
      result = active.filter(c => c.assignee?.id === uid || (scope.tids.length > 0 && c.team && scope.tids.includes(c.team.id)))
    } else if (scope.hasTodos) {
      result = active
    } else {
      // Fallback: mesmo comportamento de visaoSomenteAgente
      result = active.filter(c => c.assignee?.id === uid)
    }

    return [...result].sort((a, b) => {
      const pa = a.priority && a.priority !== 'none' ? (PRIO_ORDER[a.priority] ?? 4) : 4
      const pb = b.priority && b.priority !== 'none' ? (PRIO_ORDER[b.priority] ?? 4) : 4
      return pa - pb
    })
  })

  return { myConvs }
}
